import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { TriggerProps } from '@atlaskit/popup/types';
import { publish, useSubscribe } from '@atlaskit/rovo-triggers/main';
import { Topics, type Payload } from '@atlaskit/rovo-triggers/types';

import { RemixContainer } from '@atlassian/malleable-ui-components/remix';
import type { RemixHiddenOptionKind } from '@atlassian/malleable-ui-components/common/types';
import {
	PAGE_REMIX_OPTIONS,
	getTypeDisplayName,
} from '@atlassian/malleable-ui-components/remix/remix-overlay/utils';
import remixOverlayMessages from '@atlassian/malleable-ui-components/remix/remix-overlay/messages';
import type {
	RemixOptionSelectionParameters,
	RemixOptionKind,
} from '@atlassian/malleable-ui-components/remix/remix-overlay/types';
import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import {
	ExperienceName,
	REMIX_GENERATE_PREVIEW_MARKS,
	remixGeneratePreviewExperience,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	abortRemixCreateExperience,
	abortRemixCreateExperienceIfNotCommitted,
	startRemixCreateExperience,
	succeedRemixCreateExperienceAtHandoff,
	trackRemixCreateOptionSelected,
} from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';
import type {
	EditorRemixLifecycleEndReason,
	RemixContentComponentProps,
} from '@atlassian/malleable-ui-components/types';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { expValEquals, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { useContentType, usePageContentId } from '@confluence/page-context';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import { resolveRemixDynamicUiContentType, type DynamicUiSource } from './common-types.ts';
import { flattenDocContentForPageRemixFragment } from './flattenDocContentForPageRemixFragment.ts';
import {
	prepareObjectSidebarPageRemixStart,
	runObjectSidebarPageRemixNativeStart,
	type ObjectSidebarPageRemixStartRejectionReason,
} from './ObjectSidebarPageRemixStart.ts';
import { useObjectSidebarRemixLifecycleDiagnostics } from './useObjectSidebarRemixLifecycleDiagnostics.ts';
import { useRemixThinkingBarProgress } from './useRemixThinkingBarProgress.ts';
import { useRemixGenerationAnalytics } from './useRemixGenerationAnalytics.ts';
import {
	useShouldHideAttachmentRemixOptions,
	useShouldHideDiagramRemixOption,
} from './useShouldHideDiagramRemixOption.ts';
import { useRemixToAIFC } from './useRemixToAIFC.ts';

const PAGE_REMIX_ENTRY_POINT = 'objectSidebar';
const PAGE_REMIX_SOURCE = 'remixModalFromObjectLevelSidebar';
export const PAGE_REMIX_CONSUME_ONCE_KEY = 'renderer-page-remix';
const EMPTY_PAGE_ADF: DocNode = { type: 'doc', version: 1, content: [] };

const publishPageRemixHandoffFailed = () => {
	publish(Topics.ROVO_REMIX, {
		type: 'rovo-remix-handoff-failed',
		source: PAGE_REMIX_CONSUME_ONCE_KEY,
	} as unknown as Payload);
};

const isPageSubmit = (payload: Payload): boolean =>
	payload.type === 'submit-rovo-remix' && payload.data.remixPreset === 'page';

const isRequestRovoPageRemix = (payload: Payload): boolean =>
	(payload as { type: string }).type === 'request-rovo-page-remix';

const isEmptyPageAdf = (doc: DocNode | null | undefined): boolean => {
	if (!doc || doc.type !== 'doc') {
		return true;
	}
	const content = doc.content;
	if (!content || content.length === 0) {
		return true;
	}
	return content.every(
		(node) =>
			node.type === 'paragraph' &&
			(!('content' in node) || !node.content || node.content.length === 0),
	);
};

type PageRemixRequestParameters = RemixOptionSelectionParameters & { conversationId?: string };

type PreparedPageRemixRequest =
	| {
			status: 'started';
			params: PageRemixRequestParameters;
			lifecycleAdmission: PageRemixLifecycleAdmissionOutcome;
	  }
	| {
			status: 'rejected';
			reason: ObjectSidebarPageRemixStartRejectionReason;
	  };

type PageRemixStartEntryPath = 'popup-option' | 'rovo-sidebar';

// TODO: Consolidate with ListenForSelectedContentRemixSubmit. Both listen for a Rovo Remix
// submit, start the existing native Remix path once, and stand down if the user closes Rovo,
// starts a new chat, opens a different Remix entry, or navigates. They share that skeleton and
// should be one component, even though one handles the whole page and the other handles
// selected text.
/** Listens for whole-page Rovo Remix submit and starts the existing page remix path. */
const PageRemixSubmitListener = ({
	currentContentId,
	hasGenerationStarted,
	onAcceptSubmit,
	onDisarm,
	originContentIdRef,
	isRemixLifecycleTreatmentEnabled,
}: {
	currentContentId: string | undefined;
	hasGenerationStarted: boolean;
	onAcceptSubmit: (params: PageRemixRequestParameters) => Promise<void>;
	onDisarm: () => void;
	originContentIdRef: React.MutableRefObject<string | undefined>;
	isRemixLifecycleTreatmentEnabled?: boolean;
}) => {
	const hasGenerationStartedRef = useRef(hasGenerationStarted);
	hasGenerationStartedRef.current = hasGenerationStarted;
	const acceptedSubmitRef = useRef(false);
	const isArmedRef = useRef(false);
	const onAcceptSubmitRef = useRef(onAcceptSubmit);
	onAcceptSubmitRef.current = onAcceptSubmit;
	const onDisarmRef = useRef(onDisarm);
	onDisarmRef.current = onDisarm;

	const reportLifecycleDiagnostic = useObjectSidebarRemixLifecycleDiagnostics();
	const logRequestRovoPageRemixObserved = useCallback(
		(payload: Payload) => {
			reportLifecycleDiagnostic('observed', {
				operation: 'requestRovoPageRemix',
				entryPath: (payload as { source?: unknown }).source === 'editor' ? 'editor' : 'unknown',
			});
		},
		[reportLifecycleDiagnostic],
	);

	const disarmIfIdle = useCallback(() => {
		if (hasGenerationStartedRef.current || acceptedSubmitRef.current) {
			return;
		}
		isArmedRef.current = false;
		originContentIdRef.current = undefined;
		onDisarmRef.current();
	}, [originContentIdRef]);

	const acceptPageRequest = useCallback(
		(params: PageRemixRequestParameters) => {
			const originContentId = originContentIdRef.current;
			if (originContentId && currentContentId && originContentId !== currentContentId) {
				return;
			}

			if (acceptedSubmitRef.current || hasGenerationStartedRef.current) {
				return;
			}

			acceptedSubmitRef.current = true;
			void onAcceptSubmitRef.current(params).catch(() => {
				acceptedSubmitRef.current = false;
			});
		},
		[currentContentId, originContentIdRef],
	);

	useSubscribe(
		{ topic: Topics.ROVO_REMIX, consumeOnceKey: PAGE_REMIX_CONSUME_ONCE_KEY },
		(payload) => {
			if (payload.type === 'update-rovo-remix-preset') {
				if (payload.data.remixPreset === 'page') {
					isArmedRef.current = true;
					acceptedSubmitRef.current = false;
					originContentIdRef.current = currentContentId;
				} else {
					disarmIfIdle();
				}
				return;
			}

			if (isRequestRovoPageRemix(payload)) {
				const data = (payload as { data?: Record<string, unknown> }).data ?? {};
				const type = (data.remixOption ?? data.type) as RemixOptionKind | undefined;
				if (isRemixLifecycleTreatmentEnabled) {
					logRequestRovoPageRemixObserved(payload);
				}
				if (!type) {
					return;
				}
				acceptPageRequest({
					type,
					subType: (data.remixSubType ?? data.subType) as string | undefined,
					userPrompt: data.userPrompt as string | undefined,
					conversationId: data.conversationId as string | undefined,
				});
				return;
			}

			if (!isPageSubmit(payload) || payload.type !== 'submit-rovo-remix') {
				return;
			}

			if (!isArmedRef.current) {
				return;
			}

			acceptPageRequest({
				type: payload.data.remixOption,
				subType: payload.data.remixSubType,
				userPrompt: payload.data.userPrompt,
				conversationId: payload.data.conversationId,
			});
		},
	);

	useSubscribe({ topic: Topics.AI_MATE }, (payload) => {
		if (payload.type === 'chat-close') {
			disarmIfIdle();
			return;
		}

		if (payload.type === 'chat-new' && payload.data.resetActiveMenu) {
			disarmIfIdle();
			return;
		}

		if (payload.type !== 'open-rovo-remix-sidebar') {
			return;
		}

		if (payload.data.remixPreset === 'page') {
			isArmedRef.current = true;
			acceptedSubmitRef.current = false;
			originContentIdRef.current = currentContentId;
			return;
		}

		disarmIfIdle();
	});

	useEffect(() => {
		const originContentId = originContentIdRef.current;
		if (originContentId && currentContentId && originContentId !== currentContentId) {
			disarmIfIdle();
		}
	}, [currentContentId, disarmIfIdle, originContentIdRef]);

	return null;
};

const PAGE_REMIX_OBJECT_CONTENT_TYPES: Partial<Record<RemixOptionKind, string>> = {
	slides: 'slides',
	whiteboard: 'whiteboard',
	database: 'database',
};

const getPageRemixObjectCreationContext = (type: RemixOptionKind) => {
	if (type === 'infographic') {
		return { experience: 'remix_object' as const, blocks: ['infographic'] };
	}

	const contentType = PAGE_REMIX_OBJECT_CONTENT_TYPES[type];
	return contentType
		? { experience: 'remix_object' as const, contentTypes: [contentType] }
		: undefined;
};

const constructPageDynamicUiSource = (
	adfDocument: DocNode,
	contentType: DynamicUiSource['contentType'],
): DynamicUiSource => ({
	contentType,
	startLocalId: '',
	endLocalId: '',
	startIndex: 0,
	endIndex: -1,
	fragmentAdf: flattenDocContentForPageRemixFragment(adfDocument),
	fullAdf: adfDocument,
});

/** Remix types that open the AIFC staging modal directly, skipping the thinking bar. */
const OPEN_AIFC_MODAL_TYPES = new Set(['slides', 'whiteboard', 'database']);

/**
 * Remix types whose flow never shows the thinking bar, because they hand off somewhere else
 * instead of waiting on a preview.
 *
 * Shared by the `skipThinkingTypes` prop and the Rovo submit path so the two cannot disagree
 * about which types show the bar. Rebuilt per call so it always reflects the current
 * experiment value.
 */
const getPageRemixSkipThinkingTypes = (): Set<string> =>
	new Set([
		...OPEN_AIFC_MODAL_TYPES,
		'infographic',
		...(expValEquals('cc_maui_polish_changes_batch_5', 'isEnabled', true) ? [] : ['custom']),
	]);

/**
 * Maps page-level remix types to the forcedContentType value expected by the
 * backend.
 */
const PAGE_REMIX_FORCED_CONTENT_TYPE: Record<string, string> = {
	slides: 'slide',
	whiteboard: 'whiteboard',
	database: 'database',
};

type RemixAifcHandoffExperienceAttributes = {
	featureArea: string;
	entryPoint: InteractionSource;
	remixType: RemixOptionSelectionParameters['type'];
	remixSubtype?: string;
	aifcObjectType?: string;
	conversationChannelId?: string;
};

const getRemixAifcHandoffExperienceAttributes = ({
	type,
	subType,
	interactionSource,
	forcedContentType,
}: {
	type: RemixOptionSelectionParameters['type'];
	subType: string | undefined;
	interactionSource: InteractionSource;
	forcedContentType: string | undefined;
}): RemixAifcHandoffExperienceAttributes => ({
	featureArea:
		getRemixFeatureArea({
			remixType: type,
			existingFeatureArea: 'remix',
		}) ?? 'remix',
	entryPoint: interactionSource,
	remixType: type,
	...(subType && { remixSubtype: subType }),
	...(forcedContentType && { aifcObjectType: forcedContentType }),
});

export type PageRemixModalProps = {
	adfDocument?: DocNode | Promise<DocNode>;
	loadAdfDocument?: () => Promise<DocNode | null>;
	currentContentId?: string;
	interactionSource?: InteractionSource;
	onClose: (options?: PageRemixCloseOptions) => void;
	onStopThinking: (options?: { reopenModal?: boolean }) => void;
	isEphemeralViewVisible: boolean;
	/** Render function for the trigger element that the popup anchors to. */
	trigger?: (triggerProps: TriggerProps) => React.ReactNode;
	/** Popup placement relative to the trigger. Defaults to 'left'. */
	popupPlacement?: 'left-end';
	/** Popup offset from the trigger in [along, away] px. */
	popupOffset?: [number, number];
	/** Whether the popup is open. */
	isPopupOpen?: boolean;
	/** When this value changes the popup will reposition itself. */
	popupRepositionKey?: number;
	/** Show the page-level title and subtitle above the first cascading-menu column. */
	showCascadingHeader?: boolean;
	presentation?: 'popup' | 'modal';
	/**
	 * Called when the sidebar should open with a specific conversation.
	 * Must be invoked from outside the ConfluenceConversationAssistant
	 * wrapper so the chat-open event reaches the sidebar's store.
	 */
	onOpenSidebarConversation?: (conversationId: string) => void;
	prefetchedAttachmentHiddenOptions?: readonly RemixHiddenOptionKind[];
	onAdmitRemixLifecycle?: (request: PageRemixLifecycleAdmissionRequest) => boolean;
	onAdmitRetainedRemixLifecycle?: (
		request: PageRemixLifecycleAdmissionRequest,
	) => PageRemixLifecycleAdmissionOutcome;
	isRemixLifecycleTreatmentEnabled?: boolean;
	shouldWaitForRemixHandoffBeforeClose?: boolean;
};

export type PageRemixCloseOptions = {
	remixLifecycleEndReason?: EditorRemixLifecycleEndReason;
};

export type PageRemixLifecycleAdmissionRequest = {
	conversationId?: string;
};

export type PageRemixLifecycleAdmissionOutcome =
	| { status: 'retained'; conversationId: string }
	| { status: 'legacy' }
	| { status: 'rejected' };

type PageRemixModalImplementationProps = PageRemixModalProps & {
	isLifecycleTreatmentEnabled: boolean;
	onPageRemixStartRejected?: (
		reason: ObjectSidebarPageRemixStartRejectionReason,
		entryPath: PageRemixStartEntryPath,
	) => void;
};

const PageRemixLifecycleNavigationGuard = ({
	cancelActiveRemixRequest,
	currentContentId,
	onClose,
	originContentId,
	stopThinking,
}: {
	cancelActiveRemixRequest: () => void;
	currentContentId: string | undefined;
	onClose: (options?: PageRemixCloseOptions) => void;
	originContentId: string | undefined;
	stopThinking: () => void;
}): null => {
	useEffect(() => {
		if (!originContentId || !currentContentId || originContentId === currentContentId) {
			return;
		}

		cancelActiveRemixRequest();
		stopThinking();
		onClose({ remixLifecycleEndReason: 'cancelled' });
	}, [cancelActiveRemixRequest, currentContentId, onClose, originContentId, stopThinking]);

	return null;
};

const PageRemixModalImplementation = ({
	adfDocument,
	loadAdfDocument,
	currentContentId,
	interactionSource = 'remixModalFromObjectLevelSidebar',
	onClose,
	onStopThinking,
	isEphemeralViewVisible,
	trigger,
	popupPlacement,
	popupOffset,
	isPopupOpen,
	popupRepositionKey,
	showCascadingHeader = false,
	presentation = 'popup',
	onOpenSidebarConversation,
	prefetchedAttachmentHiddenOptions,
	onAdmitRemixLifecycle,
	onAdmitRetainedRemixLifecycle,
	isRemixLifecycleTreatmentEnabled,
	shouldWaitForRemixHandoffBeforeClose = false,
	isLifecycleTreatmentEnabled,
	onPageRemixStartRejected,
}: PageRemixModalImplementationProps): React.JSX.Element => {
	const [pageContentId] = usePageContentId();
	const resolvedContentId = currentContentId ?? pageContentId;
	const [currentContentType] = useContentType();
	const remixDynamicUiContentType = resolveRemixDynamicUiContentType(currentContentType);
	const isRemixCancelled = useRef(false);
	const hasCommittedRemixCreateTerminal = useRef(false);
	const isDirectOpen = useRef(false);
	const activeAdfRequestId = useRef(0);
	const pendingCustomSidebarConversationIdRef = useRef<string | undefined>(undefined);
	const generationConversationChannelIdRef = useRef<string | undefined>(undefined);
	const remixOriginContentIdRef = useRef<string | undefined>(undefined);
	const resolvedContentIdRef = useRef(resolvedContentId);
	resolvedContentIdRef.current = resolvedContentId;
	const pendingResolvedAdfRef = useRef<DocNode | undefined>(undefined);
	const [isThinking, setIsThinking] = useState(false);
	const openAIFCStagingModalRef = useRef<() => void>(() => {});
	const remixAifcHandoffExperienceAttributesRef = useRef<
		RemixAifcHandoffExperienceAttributes | undefined
	>();

	const experienceTracker = useAIMateExperienceTracker();
	const {
		trackGenerationFailed,
		trackGenerationStarted,
		trackGenerationStopped,
		trackGenerationSucceeded,
		trackRemixSubmitted,
	} = useRemixGenerationAnalytics();

	const abortRemixAifcHandoffExperience = useCallback(
		(reason: string) => {
			const attributes = remixAifcHandoffExperienceAttributesRef.current;
			if (!attributes) {
				return;
			}

			experienceTracker.abort({
				name: ExperienceName.REMIX_AIFC_HANDOFF,
				reason,
				attributes: { ...attributes, reason },
			});
			remixAifcHandoffExperienceAttributesRef.current = undefined;
		},
		[experienceTracker],
	);

	const cancelActiveRemixRequest = useCallback(() => {
		isRemixCancelled.current = true;
		activeAdfRequestId.current += 1;
		abortRemixAifcHandoffExperience('User cancelled Remix AIFC handoff');
	}, [abortRemixAifcHandoffExperience]);

	const unavailableSpaceOptions = useShouldHideDiagramRemixOption();
	const unavailableAttachmentOptions = useShouldHideAttachmentRemixOptions({
		enabled: prefetchedAttachmentHiddenOptions === undefined,
		contentId: resolvedContentId,
	});
	const attachmentHiddenOptions = prefetchedAttachmentHiddenOptions ?? unavailableAttachmentOptions;
	const hiddenOptions =
		unavailableSpaceOptions.length > 0
			? [...attachmentHiddenOptions, ...unavailableSpaceOptions]
			: attachmentHiddenOptions;

	/**
	 * Ends the flow. Use this instead of calling `onStopThinking` directly: `isThinking` has to be
	 * cleared on every path that ends the flow, or the thinking bar it drives is left behind.
	 */
	const stopThinking = useCallback(
		(options?: { reopenModal?: boolean }) => {
			setIsThinking(false);
			onStopThinking(options);
		},
		[onStopThinking],
	);

	const openModalAndCloseOverlay = useCallback(() => {
		hasCommittedRemixCreateTerminal.current = true;
		const pendingCustomSidebarConversationId = pendingCustomSidebarConversationIdRef.current;
		if (pendingCustomSidebarConversationId) {
			pendingCustomSidebarConversationIdRef.current = undefined;
			onOpenSidebarConversation?.(pendingCustomSidebarConversationId);
			stopThinking();
			onClose({ remixLifecycleEndReason: 'handed-off' });
			return;
		}

		openAIFCStagingModalRef.current();
		stopThinking();
		onClose({ remixLifecycleEndReason: 'handed-off' });
	}, [onClose, onOpenSidebarConversation, stopThinking]);

	const {
		showProgressBar,
		isComplete,
		onCompleteAnimationFinished,
		signalActionReady,
		resetCompletion,
	} = useRemixThinkingBarProgress({ onOpenPreview: openModalAndCloseOverlay });

	const onActionReady = useCallback(() => {
		if (isRemixCancelled.current) {
			return;
		}
		const conversationChannelId = generationConversationChannelIdRef.current;
		generationConversationChannelIdRef.current = undefined;
		trackGenerationSucceeded(
			conversationChannelId === undefined ? undefined : { conversationChannelId },
		);
		// For types in OPEN_AIFC_MODAL_TYPES, the modal is
		// already open — skip reopening it via onActionReady.
		if (isDirectOpen.current) {
			return;
		}
		remixGeneratePreviewExperience.mark(REMIX_GENERATE_PREVIEW_MARKS.ACTION_COMPLETE_RECEIVED);
		experienceTracker.succeed({ name: ExperienceName.REMIX_START });
		void remixGeneratePreviewExperience.success();
		signalActionReady();
	}, [signalActionReady, experienceTracker, trackGenerationSucceeded]);

	const {
		sendAIFCPrompt,
		stopRemixing,
		isRemixError,
		isRemixAborted,
		openAIFCStagingModal,
		closeAIFCStagingModal,
	} = useRemixToAIFC({
		onActionReady,
		stagingAreaTargetMode: 'generating-from-content',
	});
	openAIFCStagingModalRef.current = openAIFCStagingModal;

	const experienceTrackerRef = useRef(experienceTracker);
	experienceTrackerRef.current = experienceTracker;
	useEffect(() => {
		return () => {
			activeAdfRequestId.current += 1;
			experienceTrackerRef.current.abort({
				name: ExperienceName.REMIX_START,
				reason: 'User closed remix overlay',
				attributes: { reason: 'User closed remix overlay' },
			});
			abortRemixCreateExperienceIfNotCommitted(
				experienceTrackerRef.current,
				hasCommittedRemixCreateTerminal.current,
			);
			void remixGeneratePreviewExperience.abort({
				metadata: { reason: 'User closed remix overlay' },
			});
			if (!isDirectOpen.current) {
				abortRemixAifcHandoffExperience('User closed remix overlay');
			}
		};
	}, [abortRemixAifcHandoffExperience]);

	const handleClose = useCallback(() => {
		cancelActiveRemixRequest();
		onClose({ remixLifecycleEndReason: 'cancelled' });
	}, [cancelActiveRemixRequest, onClose]);

	const onRemixFlowActiveChange = useCallback(
		(active: boolean, options?: { shouldShowThinkingBar?: boolean }) => {
			if (active) {
				if (shouldWaitForRemixHandoffBeforeClose) {
					hasCommittedRemixCreateTerminal.current = false;
				}
				isRemixCancelled.current = false;
				isDirectOpen.current = false;
				pendingCustomSidebarConversationIdRef.current = undefined;
				if (options?.shouldShowThinkingBar ?? true) {
					setIsThinking(true);
				}
				// Clear stale completion state from any previous flow.
				resetCompletion();
			} else {
				cancelActiveRemixRequest();
				isDirectOpen.current = false;
				pendingCustomSidebarConversationIdRef.current = undefined;
				experienceTracker.abort({
					name: ExperienceName.REMIX_START,
					reason: 'User cancelled remix thinking',
					attributes: { reason: 'User cancelled remix thinking' },
				});
				abortRemixCreateExperience(experienceTracker, 'User cancelled remix thinking');
				void remixGeneratePreviewExperience.abort({
					metadata: { reason: 'User cancelled remix thinking' },
				});
				trackGenerationStopped();
				stopRemixing();
				const restartAttributes = {
					entryPoint: PAGE_REMIX_ENTRY_POINT,
					remixSource: 'cancelledRemixThinking',
					originalRemixSource: PAGE_REMIX_SOURCE,
					product: 'confluence',
				};
				experienceTracker.start({
					name: ExperienceName.REMIX_START,
					id: ExperienceName.REMIX_START,
					attributes: restartAttributes,
				});
				startRemixCreateExperience({ experienceTracker, attributes: restartAttributes });
				stopThinking(shouldWaitForRemixHandoffBeforeClose ? undefined : { reopenModal: true });
				if (shouldWaitForRemixHandoffBeforeClose) {
					onClose({ remixLifecycleEndReason: 'cancelled' });
				}
			}
		},
		[
			stopRemixing,
			stopThinking,
			experienceTracker,
			resetCompletion,
			cancelActiveRemixRequest,
			trackGenerationStopped,
			onClose,
			shouldWaitForRemixHandoffBeforeClose,
		],
	);

	const prevIsRemixError = useRef(isRemixError);
	useEffect(() => {
		if (!prevIsRemixError.current && isRemixError && !isRemixCancelled.current) {
			experienceTracker.fail({
				name: ExperienceName.REMIX_START,
				error: new Error('Rovo agent error during remix'),
			});
			void remixGeneratePreviewExperience.failure({
				metadata: { reason: 'Rovo agent error during remix' },
			});
			trackGenerationFailed();
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- ends the flow in response to Rovo reporting an error, not to a local state change
			stopThinking({ reopenModal: true });
		}
		prevIsRemixError.current = isRemixError;
	}, [isRemixError, stopThinking, experienceTracker, trackGenerationFailed]);

	useEffect(() => {
		if (
			!isRemixCancelled.current &&
			isRemixError &&
			(!shouldWaitForRemixHandoffBeforeClose || !hasCommittedRemixCreateTerminal.current)
		) {
			if (shouldWaitForRemixHandoffBeforeClose) {
				hasCommittedRemixCreateTerminal.current = true;
			}
			if (isEphemeralViewVisible) {
				// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- tears down the flow in response to Rovo reporting an error, not to a local state change
				closeAIFCStagingModal();
			}
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- tears down the flow in response to Rovo reporting an error, not to a local state change
			stopThinking();
			onClose({ remixLifecycleEndReason: 'error' });
		}
	}, [
		isRemixError,
		isEphemeralViewVisible,
		closeAIFCStagingModal,
		stopThinking,
		onClose,
		shouldWaitForRemixHandoffBeforeClose,
	]);

	// Dismiss the thinking bar and close the popup when the user stops the remix
	// from the Rovo side chat.
	useEffect(() => {
		if (
			isRemixAborted &&
			!isRemixCancelled.current &&
			(!shouldWaitForRemixHandoffBeforeClose || !hasCommittedRemixCreateTerminal.current)
		) {
			if (shouldWaitForRemixHandoffBeforeClose) {
				hasCommittedRemixCreateTerminal.current = true;
			}
			const reason = 'User stopped from Rovo side chat';
			experienceTracker.abort({
				name: ExperienceName.REMIX_START,
				reason,
				attributes: { reason },
			});
			abortRemixCreateExperience(experienceTracker, reason);
			void remixGeneratePreviewExperience.abort({
				metadata: { reason },
			});
			trackGenerationStopped();
			if (isEphemeralViewVisible) {
				// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- tears down the flow in response to the user stopping the remix in Rovo, not to a local state change
				closeAIFCStagingModal();
			}
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- tears down the flow in response to the user stopping the remix in Rovo, not to a local state change
			stopThinking();
			onClose({
				remixLifecycleEndReason:
					isLifecycleTreatmentEnabled && shouldWaitForRemixHandoffBeforeClose
						? 'cancelled'
						: 'aborted',
			});
		}
	}, [
		isRemixAborted,
		isEphemeralViewVisible,
		closeAIFCStagingModal,
		stopThinking,
		onClose,
		experienceTracker,
		trackGenerationStopped,
		isLifecycleTreatmentEnabled,
		shouldWaitForRemixHandoffBeforeClose,
	]);

	const handleOptionSelect = useCallback(
		async (
			{
				type,
				subType,
				userPrompt,
				conversationId,
			}: RemixOptionSelectionParameters & { conversationId?: string },
			options?: { isMachineBackedStart?: boolean; isRovoChatSubmission?: boolean },
		) => {
			const isLifecycleTreatmentStart = options !== undefined;
			const isMachineBackedStart = options?.isMachineBackedStart ?? false;
			const isRovoChatSubmission = isLifecycleTreatmentStart
				? (options?.isRovoChatSubmission ?? false)
				: conversationId !== undefined &&
					UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant2');
			const isCustomOnlyPrompt = type === 'custom' && !subType && !!userPrompt?.trim();
			const shouldDeferCustomOnlyPrompt =
				isCustomOnlyPrompt && expValEquals('cc_maui_polish_changes_batch_5', 'isEnabled', true);
			const adfRequestId = activeAdfRequestId.current + 1;
			activeAdfRequestId.current = adfRequestId;
			const shouldReuseRovoConversation = isLifecycleTreatmentStart
				? isRovoChatSubmission && Boolean(conversationId)
				: Boolean(conversationId);

			experienceTracker.updateAttributes({
				name: ExperienceName.REMIX_START,
				attributes: {
					remixType: type,
					remixSubtype: subType,
					includesFreegenPrompt: !!userPrompt,
					...(conversationId && { conversationChannelId: conversationId }),
				},
			});
			const forcedContentType = PAGE_REMIX_FORCED_CONTENT_TYPE[type];
			const isRemixAifcHandoff = OPEN_AIFC_MODAL_TYPES.has(type);
			const remixAifcHandoffExperienceAttributes = isRemixAifcHandoff
				? getRemixAifcHandoffExperienceAttributes({
						type,
						subType,
						interactionSource,
						forcedContentType,
					})
				: undefined;

			if (remixAifcHandoffExperienceAttributes) {
				remixAifcHandoffExperienceAttributesRef.current = remixAifcHandoffExperienceAttributes;
				experienceTracker.start({
					name: ExperienceName.REMIX_AIFC_HANDOFF,
					id: ExperienceName.REMIX_AIFC_HANDOFF,
					attributes: remixAifcHandoffExperienceAttributes,
				});
			}

			void remixGeneratePreviewExperience.start();
			remixGeneratePreviewExperience.mark(REMIX_GENERATE_PREVIEW_MARKS.SUBMIT);
			remixGeneratePreviewExperience.addMetadata({
				product: 'confluence',
				interactionSource,
				remixType: type,
				remixSubtype: subType,
				entryPoint: PAGE_REMIX_ENTRY_POINT,
				contentType: remixDynamicUiContentType,
				includesFreegenPrompt: !!userPrompt,
				...(type === 'infographic'
					? {
							isConfluenceInfographicsTwoStageEnabled: fg('confluence-infographics-two-stage'),
						}
					: {}),
			});
			const generationAnalyticsAttributes = {
				contentId: resolvedContentId,
				...(conversationId && { conversationChannelId: conversationId }),
				entryPoint: PAGE_REMIX_ENTRY_POINT,
				includesFreegenPrompt: !!userPrompt,
				product: 'confluence',
				remixSource: interactionSource,
				remixSubtype: subType,
				remixType: type,
			};
			trackRemixSubmitted(generationAnalyticsAttributes);
			trackGenerationStarted(generationAnalyticsAttributes);
			generationConversationChannelIdRef.current = conversationId;

			let resolvedAdfDocument: DocNode;
			try {
				if (pendingResolvedAdfRef.current) {
					resolvedAdfDocument = pendingResolvedAdfRef.current;
					pendingResolvedAdfRef.current = undefined;
				} else {
					const sourceAdf = adfDocument ?? EMPTY_PAGE_ADF;
					resolvedAdfDocument = sourceAdf instanceof Promise ? await sourceAdf : sourceAdf;
				}
			} catch (error) {
				if (activeAdfRequestId.current !== adfRequestId) {
					return;
				}

				if (
					UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant2')
				) {
					publishPageRemixHandoffFailed();
				}

				experienceTracker.fail({
					name: ExperienceName.REMIX_START,
					error: error instanceof Error ? error : new Error('Failed to load content for Remix'),
				});
				void remixGeneratePreviewExperience.failure({
					metadata: { reason: 'Failed to load content for Remix' },
				});
				trackGenerationFailed();
				if (remixAifcHandoffExperienceAttributes) {
					experienceTracker.fail({
						name: ExperienceName.REMIX_AIFC_HANDOFF,
						error:
							error instanceof Error ? error : new Error('Failed to prepare Remix AIFC handoff'),
						attributes: remixAifcHandoffExperienceAttributes,
					});
					remixAifcHandoffExperienceAttributesRef.current = undefined;
				}
				stopThinking();
				handleClose();
				return;
			}

			if (activeAdfRequestId.current !== adfRequestId) {
				return;
			}

			const dynamicUiSource = constructPageDynamicUiSource(
				resolvedAdfDocument,
				remixDynamicUiContentType,
			);
			const additionalCreationContextParams = getPageRemixObjectCreationContext(type);
			const promptText = isRovoChatSubmission
				? userPrompt
				: type === 'slides'
					? `Remix the selected content as ${getTypeDisplayName(type)}`
					: undefined;

			const promptArgs = {
				userPrompt: isRovoChatSubmission ? undefined : userPrompt,
				promptText,
				dynamicUiType: type,
				dynamicUiSubtype: subType,
				dynamicUiSource,
				interactionSource,
				additionalCreationContextParams,
				...(conversationId ? { conversationId } : {}),
			};

			if (!OPEN_AIFC_MODAL_TYPES.has(type)) {
				isDirectOpen.current = !shouldDeferCustomOnlyPrompt;
				// A machine-backed start has to await the prompt even before the retained
				// lifecycle has propagated into `shouldWaitForRemixHandoffBeforeClose`, so a
				// native-send rejection can be rethrown and settle the lifecycle as cancelled.
				const shouldWaitForPromptBeforeClose =
					shouldReuseRovoConversation ||
					shouldWaitForRemixHandoffBeforeClose ||
					isMachineBackedStart;
				const promptPromise = sendAIFCPrompt(promptArgs).then((conversationChannelId) => {
					generationConversationChannelIdRef.current = conversationChannelId;
					if (conversationChannelId && !isRemixCancelled.current && !shouldReuseRovoConversation) {
						if (shouldDeferCustomOnlyPrompt) {
							pendingCustomSidebarConversationIdRef.current = conversationChannelId;
						} else {
							onOpenSidebarConversation?.(conversationChannelId);
						}
					}
					trackRemixCreateOptionSelected({
						experienceTracker,
						conversationId: conversationChannelId,
						remixType: type,
						remixSubtype: subType,
						includesFreegenPrompt: !!userPrompt,
					});
					return conversationChannelId;
				});

				let conversationChannelId: string | undefined;
				if (shouldWaitForPromptBeforeClose) {
					try {
						conversationChannelId = await promptPromise;
					} catch (error) {
						if (
							UNSAFE_expValEqualsNoExposure(
								'cc-ce-maui-remix-menu-multivariant',
								'cohort',
								'variant2',
							)
						) {
							publishPageRemixHandoffFailed();
						}
						trackGenerationFailed();
						if (shouldReuseRovoConversation || isMachineBackedStart) {
							throw error;
						}
						return;
					}
				} else {
					void promptPromise.catch(() => {
						if (
							UNSAFE_expValEqualsNoExposure(
								'cc-ce-maui-remix-menu-multivariant',
								'cohort',
								'variant2',
							)
						) {
							publishPageRemixHandoffFailed();
						}
					});
				}

				if (!shouldDeferCustomOnlyPrompt) {
					if (shouldWaitForPromptBeforeClose && !conversationChannelId) {
						return;
					}
					experienceTracker.succeed({ name: ExperienceName.REMIX_START });
					void remixGeneratePreviewExperience.success();
					if (!shouldReuseRovoConversation) {
						hasCommittedRemixCreateTerminal.current = true;
						// The popup-mode RemixContainer stays mounted after close and shows its thinking bar
						// whenever thinking is on, so end thinking here or the bar outlives the handoff.
						stopThinking();
						onClose({ remixLifecycleEndReason: 'handed-off' });
					}
				}
			} else {
				isDirectOpen.current = true;
				const promptPromise = sendAIFCPrompt({
					...promptArgs,
					forcedContentType,
				})
					.then((conversationChannelId) => {
						generationConversationChannelIdRef.current = conversationChannelId;
						trackRemixCreateOptionSelected({
							experienceTracker,
							conversationId: conversationChannelId,
							remixType: type,
							remixSubtype: subType,
							includesFreegenPrompt: !!userPrompt,
						});
						if (!isRemixCancelled.current) {
							const attributes = {
								...remixAifcHandoffExperienceAttributes,
								...(conversationChannelId && { conversationChannelId }),
							};
							experienceTracker.succeed({
								name: ExperienceName.REMIX_AIFC_HANDOFF,
								attributes,
							});
							succeedRemixCreateExperienceAtHandoff(experienceTracker, {
								...remixAifcHandoffExperienceAttributes,
								...(conversationChannelId && { conversationId: conversationChannelId }),
							});
							remixAifcHandoffExperienceAttributesRef.current = undefined;
							experienceTracker.succeed({ name: ExperienceName.REMIX_START });
							void remixGeneratePreviewExperience.success();
							openAIFCStagingModal({ forceFullModal: true });
						}
						return conversationChannelId;
					})
					.catch((error) => {
						if (!isRemixCancelled.current) {
							if (
								UNSAFE_expValEqualsNoExposure(
									'cc-ce-maui-remix-menu-multivariant',
									'cohort',
									'variant2',
								)
							) {
								publishPageRemixHandoffFailed();
							}
							const handoffError =
								error instanceof Error ? error : new Error('Failed to complete Remix AIFC handoff');

							experienceTracker.fail({
								name: ExperienceName.REMIX_AIFC_HANDOFF,
								error: handoffError,
								attributes: remixAifcHandoffExperienceAttributes,
							});
							experienceTracker.fail({
								name: ExperienceName.REMIX_START,
								error: handoffError,
							});
							void remixGeneratePreviewExperience.failure({
								metadata: { reason: 'Failed to complete Remix AIFC handoff' },
							});
							trackGenerationFailed();
							remixAifcHandoffExperienceAttributesRef.current = undefined;
						}
						if (shouldReuseRovoConversation || isMachineBackedStart) {
							throw error;
						}
						return undefined;
					});
				const shouldWaitForPromptBeforeClose =
					shouldReuseRovoConversation ||
					shouldWaitForRemixHandoffBeforeClose ||
					isMachineBackedStart;
				if (shouldWaitForPromptBeforeClose) {
					try {
						const conversationChannelId = await promptPromise;
						if (!conversationChannelId) {
							return;
						}
					} catch (error) {
						if (shouldReuseRovoConversation || isMachineBackedStart) {
							throw error;
						}
						return;
					}
				}
				hasCommittedRemixCreateTerminal.current = true;
				// The popup-mode RemixContainer stays mounted after close and shows its thinking bar
				// whenever thinking is on, so end thinking here or the bar outlives the handoff.
				stopThinking();
				onClose({ remixLifecycleEndReason: 'handed-off' });
			}
		},
		[
			sendAIFCPrompt,
			adfDocument,
			interactionSource,
			openAIFCStagingModal,
			onClose,
			stopThinking,
			experienceTracker,
			onOpenSidebarConversation,
			handleClose,
			remixDynamicUiContentType,
			resolvedContentId,
			shouldWaitForRemixHandoffBeforeClose,
			trackGenerationFailed,
			trackGenerationStarted,
			trackRemixSubmitted,
		],
	);

	const preparePageRemixRequest = useCallback(
		async (
			params: PageRemixRequestParameters,
			entryPath: PageRemixStartEntryPath,
		): Promise<PreparedPageRemixRequest> => {
			const startRequestId = activeAdfRequestId.current + 1;
			activeAdfRequestId.current = startRequestId;
			const originContentId = resolvedContentId;
			const admission: { outcome: PageRemixLifecycleAdmissionOutcome } = {
				outcome: { status: 'legacy' },
			};
			const startResult = await prepareObjectSidebarPageRemixStart({
				resolveAdfDocument: async () => {
					if (loadAdfDocument) {
						return loadAdfDocument();
					}
					if (adfDocument instanceof Promise) {
						return adfDocument;
					}
					return adfDocument ?? null;
				},
				admitLifecycle: onAdmitRetainedRemixLifecycle
					? (request) => {
							const lifecycleAdmission = onAdmitRetainedRemixLifecycle(request);
							admission.outcome = lifecycleAdmission;
							return lifecycleAdmission.status !== 'rejected';
						}
					: undefined,
				conversationId: params.conversationId,
				isCurrent: () =>
					activeAdfRequestId.current === startRequestId &&
					resolvedContentIdRef.current === originContentId,
				onRejected: (reason) => onPageRemixStartRejected?.(reason, entryPath),
			});

			if (startResult.status === 'rejected') {
				return startResult;
			}

			const lifecycleAdmission = admission.outcome;
			pendingResolvedAdfRef.current = startResult.adfDocument;
			if (lifecycleAdmission.status === 'retained') {
				remixOriginContentIdRef.current = originContentId;
				return {
					status: 'started',
					lifecycleAdmission,
					params: {
						...params,
						conversationId: lifecycleAdmission.conversationId,
					},
				};
			}

			return { status: 'started', lifecycleAdmission, params };
		},
		[
			adfDocument,
			loadAdfDocument,
			onAdmitRetainedRemixLifecycle,
			onPageRemixStartRejected,
			resolvedContentId,
		],
	);

	const handleObjectSidebarOptionSelect = useCallback(
		async (params: RemixOptionSelectionParameters) => {
			const preparedRequest = await preparePageRemixRequest(params, 'popup-option');
			if (preparedRequest.status === 'rejected') {
				stopThinking({ reopenModal: true });
				return;
			}

			try {
				await runObjectSidebarPageRemixNativeStart({
					startNativeRemix: () =>
						handleOptionSelect(preparedRequest.params, {
							isMachineBackedStart: preparedRequest.lifecycleAdmission.status === 'retained',
							isRovoChatSubmission: false,
						}),
					onNativeStartRejected: () => {
						stopThinking({ reopenModal: true });
						onClose({ remixLifecycleEndReason: 'cancelled' });
					},
				});
			} catch {
				// The native-start wrapper has already restored the picker and settled the lifecycle.
			}
		},
		[handleOptionSelect, onClose, preparePageRemixRequest, stopThinking],
	);

	const disarmIdlePageRemix = useCallback(() => {
		pendingResolvedAdfRef.current = undefined;
	}, []);

	const handleVariantTwoPageRemixRequest = useCallback(
		async (params: PageRemixRequestParameters) => {
			if (!isLifecycleTreatmentEnabled) {
				let resolvedAdf: DocNode | null = null;
				try {
					if (loadAdfDocument) {
						resolvedAdf = await loadAdfDocument();
					} else if (adfDocument instanceof Promise) {
						resolvedAdf = await adfDocument;
					} else {
						resolvedAdf = adfDocument ?? null;
					}
				} catch {
					publishPageRemixHandoffFailed();
					throw new Error('Failed to load page ADF for Remix');
				}
				if (!resolvedAdf || isEmptyPageAdf(resolvedAdf)) {
					publishPageRemixHandoffFailed();
					throw new Error('Page content is empty');
				}
				if (
					onAdmitRemixLifecycle &&
					!onAdmitRemixLifecycle({ conversationId: params.conversationId })
				) {
					publishPageRemixHandoffFailed();
					throw new Error('Failed to admit page Remix lifecycle');
				}

				pendingResolvedAdfRef.current = resolvedAdf;
				onRemixFlowActiveChange(true, {
					shouldShowThinkingBar: !getPageRemixSkipThinkingTypes().has(params.type),
				});
				try {
					await handleOptionSelect(params);
				} catch {
					stopThinking({ reopenModal: true });
					onClose({ remixLifecycleEndReason: 'error' });
					throw new Error('Failed to start page remix from Rovo submit');
				}
				return;
			}

			const startResult = await preparePageRemixRequest(params, 'rovo-sidebar');

			if (startResult.status === 'rejected') {
				publishPageRemixHandoffFailed();
				if (startResult.reason === 'adf-load-failed') {
					throw new Error('Failed to load page ADF for Remix');
				}
				if (startResult.reason === 'empty-adf') {
					throw new Error('Page content is empty');
				}
				if (startResult.reason === 'malformed-adf') {
					throw new Error('Page content is malformed');
				}
				if (startResult.reason === 'stale-adf') {
					throw new Error('Page content changed while preparing Remix');
				}
				throw new Error('Failed to admit page Remix lifecycle');
			}

			onRemixFlowActiveChange(true, {
				shouldShowThinkingBar: !getPageRemixSkipThinkingTypes().has(params.type),
			});
			await runObjectSidebarPageRemixNativeStart({
				startNativeRemix: () =>
					handleOptionSelect(startResult.params, {
						isMachineBackedStart: startResult.lifecycleAdmission.status === 'retained',
						isRovoChatSubmission: true,
					}),
				onNativeStartRejected: () => {
					stopThinking({ reopenModal: true });
					onClose({ remixLifecycleEndReason: 'cancelled' });
				},
			});
		},
		[
			adfDocument,
			handleOptionSelect,
			isLifecycleTreatmentEnabled,
			loadAdfDocument,
			onAdmitRemixLifecycle,
			onClose,
			onRemixFlowActiveChange,
			preparePageRemixRequest,
			stopThinking,
		],
	);

	const shouldShowExistingRemixTypeMenu =
		isThinking ||
		!UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant2');

	return (
		<>
			{isLifecycleTreatmentEnabled && shouldWaitForRemixHandoffBeforeClose && (
				<PageRemixLifecycleNavigationGuard
					cancelActiveRemixRequest={cancelActiveRemixRequest}
					currentContentId={resolvedContentId}
					onClose={onClose}
					originContentId={remixOriginContentIdRef.current}
					stopThinking={stopThinking}
				/>
			)}
			{UNSAFE_expValEqualsNoExposure(
				'cc-ce-maui-remix-menu-multivariant',
				'cohort',
				'variant2',
			) && (
				<PageRemixSubmitListener
					currentContentId={resolvedContentId}
					hasGenerationStarted={isThinking}
					onAcceptSubmit={handleVariantTwoPageRemixRequest}
					onDisarm={disarmIdlePageRemix}
					originContentIdRef={remixOriginContentIdRef}
					isRemixLifecycleTreatmentEnabled={isRemixLifecycleTreatmentEnabled}
				/>
			)}
			{shouldShowExistingRemixTypeMenu && (
				<RemixContainer
					onClose={handleClose}
					onOptionSelect={
						isLifecycleTreatmentEnabled ? handleObjectSidebarOptionSelect : handleOptionSelect
					}
					onRemixFlowActiveChange={onRemixFlowActiveChange}
					isEphemeralViewVisible={isEphemeralViewVisible}
					skipThinkingTypes={getPageRemixSkipThinkingTypes()}
					options={PAGE_REMIX_OPTIONS}
					hiddenOptions={hiddenOptions}
					subtitle={remixOverlayMessages.pageRemixSubtitle}
					cascadingMenuHeader={
						showCascadingHeader
							? {
									title: remixOverlayMessages.heading,
									subtitle: remixOverlayMessages.pageRemixSubtitle,
								}
							: undefined
					}
					presentation={presentation}
					trigger={trigger}
					popupPlacement={popupPlacement}
					popupOffset={popupOffset}
					isPopupOpen={isPopupOpen}
					popupRepositionKey={popupRepositionKey}
					showProgressBar={showProgressBar}
					isComplete={isComplete}
					onCompleteAnimationFinished={onCompleteAnimationFinished}
					isThinking={
						isLifecycleTreatmentEnabled || shouldWaitForRemixHandoffBeforeClose
							? isThinking
							: isThinking &&
								  UNSAFE_expValEqualsNoExposure(
										'cc-ce-maui-remix-menu-multivariant',
										'cohort',
										'variant2',
								  )
								? true
								: undefined
					}
				/>
			)}
		</>
	);
};

const PageRemixLifecycleTreatmentModal = (
	props: PageRemixModalProps & {
		onAdmitRetainedRemixLifecycle: NonNullable<
			PageRemixModalProps['onAdmitRetainedRemixLifecycle']
		>;
	},
): React.JSX.Element => {
	const reportLifecycleDiagnostic = useObjectSidebarRemixLifecycleDiagnostics();
	const [hasRetainedLifecycle, setHasRetainedLifecycle] = useState(false);
	const hasRetainedLifecycleRef = useRef(false);
	const {
		onAdmitRetainedRemixLifecycle: admitRetainedRemixLifecycle,
		onClose: closeTreatmentModal,
	} = props;
	// The owner swaps its close handler once a lifecycle is retained, and admission happens inside
	// the same async submit that later hands off. Delegate to the handler current at call time so
	// the in-flight flow releases the lifecycle it admitted instead of only resetting the UI.
	const closeTreatmentModalRef = useRef(closeTreatmentModal);
	closeTreatmentModalRef.current = closeTreatmentModal;
	const onClose = useCallback((options?: PageRemixCloseOptions) => {
		if (hasRetainedLifecycleRef.current) {
			hasRetainedLifecycleRef.current = false;
			setHasRetainedLifecycle(false);
		}
		closeTreatmentModalRef.current(options);
	}, []);
	const onAdmitRetainedRemixLifecycle = useCallback(
		(request: PageRemixLifecycleAdmissionRequest) => {
			const outcome = admitRetainedRemixLifecycle(request);
			if (outcome.status === 'retained') {
				hasRetainedLifecycleRef.current = true;
				setHasRetainedLifecycle(true);
			}
			return outcome;
		},
		[admitRetainedRemixLifecycle],
	);
	const onPageRemixStartRejected = useCallback(
		(reason: ObjectSidebarPageRemixStartRejectionReason, entryPath: PageRemixStartEntryPath) => {
			reportLifecycleDiagnostic('rejected', {
				operation: 'preparePageRemixStart',
				entryPath,
				rejectionReason: reason,
			});
		},
		[reportLifecycleDiagnostic],
	);

	return (
		<PageRemixModalImplementation
			{...props}
			onClose={onClose}
			onAdmitRetainedRemixLifecycle={onAdmitRetainedRemixLifecycle}
			onPageRemixStartRejected={onPageRemixStartRejected}
			shouldWaitForRemixHandoffBeforeClose={
				props.shouldWaitForRemixHandoffBeforeClose || hasRetainedLifecycle
			}
			isLifecycleTreatmentEnabled
		/>
	);
};

export const PageRemixModal = (props: PageRemixModalProps): React.JSX.Element => {
	if (props.onAdmitRetainedRemixLifecycle && props.isRemixLifecycleTreatmentEnabled) {
		return (
			<PageRemixLifecycleTreatmentModal
				{...props}
				onAdmitRetainedRemixLifecycle={props.onAdmitRetainedRemixLifecycle}
			/>
		);
	}

	return <PageRemixModalImplementation {...props} isLifecycleTreatmentEnabled={false} />;
};

export type PageRemixSubmitOwnerProps = {
	loadAdfDocument?: () => Promise<DocNode | null>;
	onOpenSidebarConversation?: (conversationId: string) => void;
	onClose?: (options?: PageRemixCloseOptions) => void;
	onStopThinking?: (options?: { reopenModal?: boolean }) => void;
	onAdmitRemixLifecycle?: (request: PageRemixLifecycleAdmissionRequest) => boolean;
	onAdmitRetainedRemixLifecycle?: (
		request: PageRemixLifecycleAdmissionRequest,
	) => PageRemixLifecycleAdmissionOutcome;
	isRemixLifecycleTreatmentEnabled?: boolean;
	shouldWaitForRemixHandoffBeforeClose?: boolean;
};

export const PageRemixSubmitOwner = ({
	loadAdfDocument,
	onOpenSidebarConversation,
	onClose = () => {},
	onStopThinking = () => {},
	onAdmitRemixLifecycle,
	onAdmitRetainedRemixLifecycle,
	isRemixLifecycleTreatmentEnabled,
	shouldWaitForRemixHandoffBeforeClose,
}: PageRemixSubmitOwnerProps): React.JSX.Element => {
	const { showSmartCreationModal } = useSmartCreationModalState();
	const [pageContentId] = usePageContentId();

	return (
		<ConfluenceConversationAssistant>
			<PageRemixModal
				loadAdfDocument={loadAdfDocument}
				currentContentId={pageContentId}
				onClose={onClose}
				onStopThinking={onStopThinking}
				isEphemeralViewVisible={showSmartCreationModal}
				onOpenSidebarConversation={onOpenSidebarConversation}
				onAdmitRemixLifecycle={onAdmitRemixLifecycle}
				onAdmitRetainedRemixLifecycle={onAdmitRetainedRemixLifecycle}
				isRemixLifecycleTreatmentEnabled={isRemixLifecycleTreatmentEnabled}
				shouldWaitForRemixHandoffBeforeClose={shouldWaitForRemixHandoffBeforeClose}
			/>
		</ConfluenceConversationAssistant>
	);
};

export const EditorPageRemixModal = (
	props: Extract<RemixContentComponentProps, { remixType: 'object' }> & {
		prefetchedAttachmentHiddenOptions?: readonly RemixHiddenOptionKind[];
	},
): React.JSX.Element => {
	const { showSmartCreationModal } = useSmartCreationModalState();
	const interactionSource = props.remixModalMetadata?.interactionSource;

	return (
		<PageRemixModal
			adfDocument={props.adfDocument ?? { type: 'doc', version: 1, content: [] }}
			onClose={props.handleModalClose}
			onStopThinking={props.onStopThinking}
			isEphemeralViewVisible={showSmartCreationModal}
			interactionSource={interactionSource}
			presentation="modal"
			prefetchedAttachmentHiddenOptions={props.prefetchedAttachmentHiddenOptions}
		/>
	);
};
